import { useEffect, useRef, useState } from 'react';
import { useQueries, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { queryKeys } from '@/api/queryKeys';
import { get, post } from '@/api/client';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { Button } from '@/components/atoms/Button';
import { toast } from '@/store/toastStore';
import { usePointStore } from '@/store/pointStore';
import type { Post } from '@/components/organisms/PostCard';
import { Skeleton } from '@/components/atoms/Skeleton';
import { Avatar } from '@/components/atoms/Avatar';
import { LevelBadge } from '@/components/atoms/LevelBadge';
import { BOARD_IDS } from '@/constants/boards';
import { COIN_NAMES_KR, stripKrw } from '@/constants/coins';
import { timeAgo, comma } from '@/lib/format';
import { ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Bitcoin } from 'lucide-react';
import { AdBanner } from '@/components/ads/AdBanner';
import { SEOHead } from '@/components/atoms/SEOHead';
import { MarketSummaryBanner } from '@/components/organisms/MarketSummaryBanner';
// [제거됨] 메인 진입 공지 팝업(NoticePopupModal) — 사용 중단
// [중단됨 2026-04-29] import { NoticeBanner } from '@/components/organisms/NoticeBanner';
// 공지는 운영/공지 게시판(board=12) 의 고정 글로 관리되며, BoardPage 의 PinnedNoticesBanner 가 전 게시판 상단 노출.

interface DiaryFeedItem {
  id: number;
  ticker: string;
  asset_type: string;
  ticker_name: string | null;
  buy_price: string | null;
  sell_price: string | null;
  quantity: string;
  buy_date: string | null;
  sell_date: string | null;
  profit: string | null;
  profit_rate: string | null;
  created_at: string;
  user_id: string;
  nickname: string;
  profile_image_url: string | null;
  level: number;
}

function diaryDisplayName(d: DiaryFeedItem): string {
  if (d.asset_type === 'coin') {
    const kr = COIN_NAMES_KR[d.ticker] ?? d.ticker_name;
    return kr ? `${kr} (${stripKrw(d.ticker)})` : stripKrw(d.ticker);
  }
  return d.ticker_name ? `${d.ticker_name}` : d.ticker;
}

/**
 * 시스템 자동생성 게시판 — 별도 섹션으로만 노출.
 * 메인의 "최신글" 통합 섹션에서는 제외 (유저 작성글만 보이도록).
 */
const SYSTEM_BOARDS: ReadonlyArray<{ id: number; slug: string; name: string }> = [
  // 주식게시판(BOARD_IDS.STOCK)·코인(BOARD_IDS.COIN) 은 2026-06-02 숨김 처리.
  //   boards.is_visible=false 와 함께 정렬. SYSTEM_BOARDS 가 빈 배열이면 관련 섹션 미렌더.
];

/** "전체 게시판" 카드 색상/이모지 매핑 — 토스 스타일 accent gradient.
    토큰이 아닌 고정 색상이지만 opacity 낮은 gradient 라 light/dark 둘 다 자연스러움. */
const BOARD_ACCENT: Record<
  string,
  { emoji?: string; icon?: typeof Bitcoin; from: string; to: string }
> = {
  diary:    { emoji: '📝', from: '#6366f1', to: '#8b5cf6' },
  stock:    { emoji: '📈', from: '#ef4444', to: '#f97316' },
  global:   { emoji: '🌏', from: '#0ea5e9', to: '#3b82f6' },
  coin:     { icon: Bitcoin, from: '#f7931a', to: '#d97706' },
  notice:   { emoji: '📰', from: '#64748b', to: '#475569' },
  news:     { emoji: '🗞️', from: '#0d9488', to: '#0891b2' },
  market:   { emoji: '📊', from: '#0284c7', to: '#1e40af' },
  free:     { emoji: '💬', from: '#10b981', to: '#059669' },
};

/** 전용 페이지로 라우팅하는 시스템 게시판 — BoardTabs SPECIAL_TARGETS 와 동일 */
const SPECIAL_BOARD_TARGETS: Record<string, string> = {
  news: '/news',
  market: '/market',
};

export function HomePage() {
  const navigate = useNavigate();
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const setLoginModalOpen = useUIStore((s) => s.setLoginModalOpen);

  const goMyDiary = () => {
    if (!isLoggedIn) return setLoginModalOpen(true);
    navigate('/diary');
  };

  /*
   * "전체 게시판" 섹션용 — `GET /boards` 동적 조회 (활성+공개 게시판만).
   * 정적 `BOARDS` 상수는 BOARD_BY_SLUG / BOARD_BY_ID 메타 룩업에만 사용 (legacy 포스트 이름 표시 포함).
   */
  interface BoardRow { id: number; slug: string; name: string }
  const boardsQuery = useQuery({
    queryKey: ['boards', 'visible'],
    queryFn: () => get<BoardRow[]>('/boards'),
    staleTime: 5 * 60_000,
  });
  /* 메인 "전체 게시판" 섹션에서 제외할 슬러그 — notice-ops/suggest 는 DB 에서 이미
     is_visible=FALSE 라 API 응답엔 없지만 admin 응답 대비로 방어. event/news 는 메인 미노출.
     ※ 공시(notice) 는 2026-04-29 DART 공시 수집 재가동으로 다시 메인 노출. */
  /* 뉴스(news)/시황(market) 은 전용 페이지로 라우팅(SPECIAL_BOARD_TARGETS) — 메인 카드 노출 OK. */
  const HIDDEN_HOME_SLUGS = new Set(['notice-ops', 'suggest', 'event']);
  /* BoardTabs 와 동일한 우선순위 정렬 — 사용자가 탭 순서로 기억하는 게시판 목록과 통일 */
  const HOME_PRIORITY_SLUGS = ['diary', 'stock', 'coin', 'news', 'free'];
  const visibleBoards = (() => {
    const list = (boardsQuery.data ?? []).filter(
      (b) => !HIDDEN_HOME_SLUGS.has(b.slug),
    );
    const priority = HOME_PRIORITY_SLUGS
      .map((slug) => list.find((b) => b.slug === slug))
      .filter((b): b is BoardRow => !!b);
    const rest = list.filter((b) => !HOME_PRIORITY_SLUGS.includes(b.slug));
    return [...priority, ...rest];
  })();

  /* 인기글 — 공시(3)/운영·공지(12)/건의·제안(13)/이벤트(14) 게시판 제외.
     (excludeAuto=true 는 시스템 자동 생성 글만 빼주므로 공시 게시판의 유저 글까지 빼려면 board id 명시 필요) */
  const popularQuery = useQuery({
    queryKey: [...queryKeys.posts.hot, 'ex-meta-notice'],
    queryFn: () =>
      get<{ posts: Post[] }>(
        '/posts?sort=hot&limit=10&excludeAuto=true&excludeBoards=3,12,13,14',
      ),
    staleTime: 60_000,
  });

  // 진행 중 이벤트 (최대 3개)
  const eventsQuery = useQuery({
    queryKey: ['events', 'home'],
    queryFn: () =>
      get<{ events: Array<{ id: number; title: string; starts_at: string; ends_at: string; reward_points: number; post_id: number | null; is_perpetual?: boolean }> }>(
        '/events',
      ),
    staleTime: 5 * 60_000,
  });

  // 최신글 — 유저 작성글만(excludeAuto=true) + 제외 게시판:
  //   1/2/3 (시스템 자동 수집: 코인시황·국내주식·공시)
  //   12/13/14 (운영·공지·건의·제안·이벤트 — 메인 최신/인기 영역에서 제외)
  // 자유게시판(11) 포함 나머지 유저 작성 보드 글은 모두 표시됨
  const latestAllQuery = useQuery({
    queryKey: ['posts', 'latest', 'user-boards-ex-meta'],
    queryFn: () =>
      get<{ posts: Post[] }>(
        '/posts?sort=latest&limit=20&excludeAuto=true&excludeBoards=1,2,3,12,13,14',
      ),
    staleTime: 30_000,
  });

  // 시스템 보드별 최신 5개 (코인시황/국내주식/공시뉴스)
  const systemBoardQueries = useQueries({
    queries: SYSTEM_BOARDS.map((b) => ({
      queryKey: queryKeys.posts.list(b.id, 'latest'),
      queryFn: () =>
        get<{ posts: Post[] }>(`/posts?boardId=${b.id}&sort=latest&limit=5`),
      staleTime: 30_000,
    })),
  });

  // 자유게시판 최신 10개 (우측 섹션 전용)
  const freeBoardQuery = useQuery({
    queryKey: queryKeys.posts.list(BOARD_IDS.FREE, 'latest'),
    queryFn: () =>
      get<{ posts: Post[] }>(`/posts?boardId=${BOARD_IDS.FREE}&sort=latest&limit=10`),
    staleTime: 30_000,
  });

  // 가이던스 분석 최신 10개 (전체 너비 섹션 전용)
  const guidanceBoardQuery = useQuery({
    queryKey: queryKeys.posts.list(BOARD_IDS.GUIDANCE_ANALYSIS, 'latest'),
    queryFn: () =>
      get<{ posts: Post[] }>(`/posts?boardId=${BOARD_IDS.GUIDANCE_ANALYSIS}&sort=latest&limit=10`),
    staleTime: 30_000,
  });

  /** 뉴스 — ai_news 최신 5건 (4 카테고리 화이트리스트 적용된 AI 자동 생성글) */
  interface NewsItem {
    id: number;
    category: string;
    title: string;
    published_at: string;
    post_id: number | null;
    source_url: string | null;
  }
  const newsQuery = useQuery({
    queryKey: ['news', 'home-latest'],
    queryFn: () => get<{ news: NewsItem[] }>('/news?limit=5'),
    staleTime: 60_000,
  });

  /** 공시 — DART 자동 수집 (board=3 NOTICE) 최신 5건 */
  const noticeQuery = useQuery({
    queryKey: queryKeys.posts.list(BOARD_IDS.NOTICE, 'latest'),
    queryFn: () =>
      get<{ posts: Post[] }>(`/posts?boardId=${BOARD_IDS.NOTICE}&sort=latest&limit=5`),
    staleTime: 60_000,
  });

  // 매매일지 공개 피드
  const diaryFeed = useQuery({
    queryKey: ['diaries', 'feed'],
    queryFn: () => get<{ feed: DiaryFeedItem[] }>('/diaries/feed'),
    staleTime: 60_000,
  });

  return (
    <div className="container-base pt-4 pb-10">
      <SEOHead
        title="시세공간 | 주식·코인·재테크 커뮤니티"
        description="AI 기반 종목 분석, 세력분석, 공시 정보, 투자 커뮤니티"
        canonical="/"
      />
      {/* [제거됨] 메인 진입 공지 팝업(NoticePopupModal) — 사용 중단 */}
      {/* 시장상황 요약 배너 — 메인 최상단 (가장 최근 발행 1건) */}
      <MarketSummaryBanner />
      {/* 히어로 + 광고 2분할 — 모바일 세로 쌓임, md+ 에서 좌(히어로 2):우(광고 1) = 2:1.
          좌/우 카드 모두 min-h-[120px] + grid items-stretch 로 높이 자동 정렬 (광고 350×120 매칭).
          - grid 자체 min-w-0 : grid 의 minmax(auto, 1fr) 기본값이 자식을 밀어내는 문제 차단
          - 자식 div 에도 min-w-0 + overflow-hidden : grid item 이 콘텐츠로 늘어나는 것 차단 */}
      <div className="mb-6 grid min-w-0 items-stretch gap-4 md:mb-10 md:grid-cols-3">
        <div className="min-w-0 overflow-hidden md:col-span-2">
          <HeroSwiper />
        </div>
        {/* 광고: 히어로 옆 고정 350×120 디스플레이 (#7 display-hero 2888078909).
            모바일은 350px 고정 광고가 컨테이너를 넘쳐 hidden, md+ 에서만 노출. */}
        <div className="hidden min-h-[120px] items-center justify-center rounded-2xl bg-[var(--color-surface)] md:flex">
          <AdBanner
            slot="display-hero"
            className="flex items-center justify-center"
          />
        </div>
      </div>

      {/* [중단됨 2026-04-29] 사이트 공지 배너 — 운영/공지 게시판(board=12) 의 고정 글로 대체.
          BoardPage 의 PinnedNoticesBanner 가 모든 게시판 상단에 동일 글을 노출함. */}
      {/* <NoticeBanner /> */}

      {/* 출석 유도 배너 */}
      <CheckinBanner />

      {/* 전체 게시판 — 토스 스타일 카드 그리드.
          모바일은 햄버거 드로어 게시판 목록과 중복되어 hidden 처리, md+ 만 노출. */}
      <section className="mb-6 hidden md:mb-10 md:block">
        <h2 className="mb-3 text-sm font-semibold text-[var(--color-text-sub)]">
          전체 게시판
        </h2>
        {/* 카드 수에 맞춰 sm+ 에서 1줄로 정렬 — 현재 공개 게시판 수에 맞춰 컬럼 수 동적 지정 */}
        <div
          className="grid grid-cols-3 gap-2 sm:[grid-template-columns:repeat(var(--sisegonggan-board-cols),minmax(0,1fr))]"
          style={
            { '--sisegonggan-board-cols': visibleBoards.length } as React.CSSProperties
          }
        >
          {visibleBoards.map((b) => {
            const accent = BOARD_ACCENT[b.slug] ?? {
              emoji: '📋',
              from: 'var(--color-text-sub)',
              to: 'var(--color-text-sub)',
            };
            const Icon = accent.icon;
            return (
              <Link
                key={b.slug}
                to={SPECIAL_BOARD_TARGETS[b.slug] ?? `/board/${b.slug}`}
                className="group relative flex min-w-0 flex-col items-center gap-1.5 overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-center transition-all duration-200 hover:-translate-y-0.5 hover:border-[var(--color-primary)]/40 sm:p-4"
              >
                {/* 아이콘 원형 — 게시판별 accent gradient */}
                <div
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-base text-white shadow-inner sm:h-11 sm:w-11"
                  style={{
                    background: `linear-gradient(135deg, ${accent.from}, ${accent.to})`,
                  }}
                >
                  {Icon ? (
                    <Icon className="h-5 w-5" strokeWidth={2.5} />
                  ) : (
                    <span>{accent.emoji}</span>
                  )}
                </div>
                <span className="block w-full truncate text-xs font-semibold text-[var(--color-text)] group-hover:text-[var(--color-primary)]">
                  {b.name}
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* 중단: 인기글(좌) + 최신글(우) 2단 */}
      <div className="mb-6 grid gap-5 md:mb-10 md:grid-cols-2">

      {/* 실시간 인기글 TOP 10 — 타이틀 좌측 아이콘 금지 (텍스트만).
          인기글(추천·댓글·조회 기준) 이 10개 미만이면 최신글로 부족분을 채워 항상 10칸 유지.
          중복은 id 기준으로 제거. */}
      {(() => {
        const popularPosts = popularQuery.data?.posts ?? [];
        const latestPosts = latestAllQuery.data?.posts ?? [];
        const popularIds = new Set(popularPosts.map((p) => p.id));
        const combined = [
          ...popularPosts,
          ...latestPosts.filter((p) => !popularIds.has(p.id)),
        ].slice(0, 10);
        return (
      <section className="min-w-0 overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 sm:p-5">
        <div className="mb-4 flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <h2 className="text-lg font-extrabold md:text-xl">실시간 인기글</h2>
          <span className="text-xs text-[var(--color-text-sub)]">추천·댓글·조회 기준</span>
        </div>
        {popularQuery.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <ol className="divide-y divide-[var(--color-border)]">
            {combined.map((p) => (
              <li key={p.id} className="min-w-0 w-full overflow-hidden">
                <Link
                  to={p.event_id ? `/events/${p.event_id}` : `/post/${p.id}`}
                  className="-mx-2 flex min-w-0 w-full items-center gap-3 overflow-hidden rounded-lg px-2 py-2.5 hover:bg-[var(--color-bg)]"
                >
                  {p.board_slug && (
                    <span
                      className="hidden shrink-0 truncate rounded bg-[var(--color-bg)] px-2 py-0.5 text-center text-xs text-[var(--color-text-sub)] sm:inline-block sm:w-20"
                      title={p.board_name}
                    >
                      {p.board_name}
                    </span>
                  )}
                  <span className="min-w-0 flex-1 truncate whitespace-nowrap overflow-hidden text-sm font-medium">{p.title}</span>
                  <span className="shrink-0 text-xs tabular-nums text-[var(--color-text-sub)]">
                    추천 {p.like_count} · 댓글 {p.comment_count}
                  </span>
                </Link>
              </li>
            ))}
            {combined.length === 0 && (
              <li className="py-6 text-center text-sm text-[var(--color-text-sub)]">
                아직 게시글이 없어요.
              </li>
            )}
          </ol>
        )}
      </section>
        );
      })()}

      {/* 자유게시판 (우측) */}
      <section className="min-w-0 overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 sm:p-5">
        <div className="mb-4 flex items-center justify-between gap-2">
          <h2 className="text-lg font-extrabold md:text-xl">자유게시판</h2>
          <Link
            to="/board/free"
            className="flex shrink-0 items-center gap-0.5 rounded-full bg-[var(--color-bg)] px-3 py-1 text-xs font-semibold text-[var(--color-text-sub)] hover:text-[var(--color-text)]"
          >
            더보기
            <ChevronRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        {(() => {
          const posts = (freeBoardQuery.data?.posts ?? []).slice(0, 10);
          if (freeBoardQuery.isLoading) {
            return (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            );
          }
          if (posts.length === 0) {
            return (
              <div className="py-6 text-center text-sm text-[var(--color-text-sub)]">
                아직 게시글이 없어요.
              </div>
            );
          }
          return (
            <ul className="divide-y divide-[var(--color-border)]">
              {posts.map((p) => (
                <li key={p.id} className="min-w-0 w-full overflow-hidden">
                  <Link
                    to={p.event_id ? `/events/${p.event_id}` : `/post/${p.id}`}
                    className="-mx-2 flex min-w-0 w-full items-center gap-2 overflow-hidden rounded-lg px-2 py-2.5 hover:bg-[var(--color-bg)]"
                  >
                    <span className="min-w-0 flex-1 truncate whitespace-nowrap overflow-hidden text-sm">{p.title}</span>
                    {p.comment_count > 0 && (
                      <span className="shrink-0 text-xs font-semibold text-[var(--color-primary)]">
                        [{p.comment_count}]
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          );
        })()}
      </section>

      </div>

      {/* 가이던스 분석 / 증시뉴스 / 공시 분석 - 3분할 (인기글/자유게시판 직후) */}
      <div className="mb-6 grid gap-5 md:mb-10 md:grid-cols-3">
        {/* 가이던스 분석 */}
        <section className="min-w-0 overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h2 className="text-lg font-extrabold md:text-xl">가이던스 분석</h2>
            <Link
              to="/board/guidance-analysis"
              className="flex shrink-0 items-center gap-0.5 rounded-full bg-[var(--color-bg)] px-3 py-1 text-xs font-semibold text-[var(--color-text-sub)] hover:text-[var(--color-text)]"
            >
              더보기
              <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          {(() => {
            const posts = guidanceBoardQuery.data?.posts ?? [];
            if (guidanceBoardQuery.isLoading) {
              return (
                <div className="space-y-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              );
            }
            if (posts.length === 0) {
              return (
                <div className="py-6 text-center text-xs text-[var(--color-text-sub)]">
                  아직 게시글이 없어요.
                </div>
              );
            }
            return (
              <ul className="divide-y divide-[var(--color-border)]">
                {posts.slice(0, 5).map((p) => (
                  <li key={p.id} className="min-w-0 w-full overflow-hidden">
                    <Link
                      to={p.event_id ? `/events/${p.event_id}` : `/post/${p.id}`}
                      className="-mx-2 flex min-w-0 w-full items-center gap-2 overflow-hidden rounded-lg px-2 py-2 hover:bg-[var(--color-bg)]"
                    >
                      <span className="min-w-0 flex-1 truncate whitespace-nowrap overflow-hidden text-sm">{p.title}</span>
                      {p.comment_count > 0 && (
                        <span className="shrink-0 text-xs font-semibold text-[var(--color-primary)]">
                          [{p.comment_count}]
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            );
          })()}
        </section>

        {/* 증시뉴스 (ai_news 최신 5건) - 카테고리 칩 없음 */}
        <section className="min-w-0 overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h2 className="text-lg font-extrabold md:text-xl">증시뉴스</h2>
            <Link
              to="/news"
              className="flex shrink-0 items-center gap-0.5 rounded-full bg-[var(--color-bg)] px-3 py-1 text-xs font-semibold text-[var(--color-text-sub)] hover:text-[var(--color-text)]"
            >
              더보기
              <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          {newsQuery.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, j) => (
                <Skeleton key={j} className="h-10 w-full" />
              ))}
            </div>
          ) : (newsQuery.data?.news?.length ?? 0) === 0 ? (
            <div className="py-6 text-center text-xs text-[var(--color-text-sub)]">
              최신 증시뉴스가 아직 없어요.
            </div>
          ) : (
            <ul className="divide-y divide-[var(--color-border)]">
              {newsQuery.data!.news.slice(0, 5).map((n) => {
                /* post_id 있으면 게시글 상세, 없으면 원문 새 탭. */
                const target = n.post_id ? `/post/${n.post_id}` : (n.source_url ?? null);
                const external = !n.post_id && !!n.source_url;
                const rowInner = (
                  <>
                    <span className="min-w-0 flex-1 truncate whitespace-nowrap overflow-hidden text-sm group-hover:underline">
                      {n.title}
                    </span>
                    <span className="shrink-0 text-xs tabular-nums text-[var(--color-text-sub)]">
                      {timeAgo(n.published_at)}
                    </span>
                  </>
                );
                return (
                  <li key={n.id} className="min-w-0 w-full overflow-hidden">
                    {target ? (
                      external ? (
                        <a
                          href={target}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group -mx-2 flex min-w-0 w-full items-center gap-2 overflow-hidden rounded-lg px-2 py-2 hover:bg-[var(--color-bg)]"
                        >
                          {rowInner}
                        </a>
                      ) : (
                        <Link
                          to={target}
                          className="group -mx-2 flex min-w-0 w-full items-center gap-2 overflow-hidden rounded-lg px-2 py-2 hover:bg-[var(--color-bg)]"
                        >
                          {rowInner}
                        </Link>
                      )
                    ) : (
                      <div className="group -mx-2 flex min-w-0 w-full items-center gap-2 overflow-hidden rounded-lg px-2 py-2 opacity-70">
                        {rowInner}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* 공시 분석 (DART, board=NOTICE 최신 5건) */}
        <section className="min-w-0 overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h2 className="text-lg font-extrabold md:text-xl">공시 분석</h2>
            <Link
              to="/board/notice"
              className="flex shrink-0 items-center gap-0.5 rounded-full bg-[var(--color-bg)] px-3 py-1 text-xs font-semibold text-[var(--color-text-sub)] hover:text-[var(--color-text)]"
            >
              더보기
              <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          {noticeQuery.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, j) => (
                <Skeleton key={j} className="h-10 w-full" />
              ))}
            </div>
          ) : (noticeQuery.data?.posts?.length ?? 0) === 0 ? (
            <div className="py-6 text-center text-xs text-[var(--color-text-sub)]">
              최신 공시 분석이 아직 없어요.
            </div>
          ) : (
            <ul className="divide-y divide-[var(--color-border)]">
              {noticeQuery.data!.posts.slice(0, 5).map((p) => {
                /* DART 자동 수집 제목은 "{종목명} - {공시제목}" 형태. " - " 없으면 전체 표시. */
                const sepIdx = p.title.indexOf(' - ');
                const company = sepIdx > 0 ? p.title.slice(0, sepIdx) : '';
                const docTitle = sepIdx > 0 ? p.title.slice(sepIdx + 3) : p.title;
                return (
                  <li key={p.id} className="min-w-0 w-full overflow-hidden">
                    <Link
                      to={`/post/${p.id}`}
                      className="group -mx-2 flex min-w-0 w-full items-center gap-2 overflow-hidden rounded-lg px-2 py-2 hover:bg-[var(--color-bg)]"
                    >
                      {company && (
                        <span className="shrink-0 truncate rounded bg-[var(--color-bg)] px-2 py-0.5 text-center text-[11px] font-semibold text-[var(--color-text-sub)] max-w-[72px]">
                          {company}
                        </span>
                      )}
                      <span className="min-w-0 flex-1 truncate whitespace-nowrap overflow-hidden text-sm group-hover:underline">
                        {docTitle}
                      </span>
                      <span className="shrink-0 text-xs tabular-nums text-[var(--color-text-sub)]">
                        {timeAgo(p.created_at)}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      {/* 광고: 3분할(가이던스/증시뉴스/공시) 과 진행 중 이벤트 사이 (#8 display-middle).
         w-[900px] + mx-auto 로 최대 900px 중앙 정렬, 모바일/좁은 뷰포트에선 max-w-full 로 자동 축소.
         min-h 확보로 AdSense 높이 판정 보조. */}
      <div className="ad-slot mx-auto mb-3 min-h-[80px] w-[900px] max-w-full overflow-hidden bg-[var(--color-surface)] sm:min-h-[120px] md:mb-10">
        <AdBanner slot="display-middle" />
      </div>

      {/* 진행 중 이벤트 — 전체 너비 1단 */}
      {(eventsQuery.data?.events?.length ?? 0) > 0 && (
        <section className="mb-6 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 sm:p-5 md:mb-10">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h2 className="text-lg font-extrabold md:text-xl">진행 중 이벤트</h2>
            <Link
              to="/events"
              className="flex shrink-0 items-center gap-0.5 rounded-full bg-[var(--color-bg)] px-3 py-1 text-xs font-semibold text-[var(--color-text-sub)] hover:text-[var(--color-text)]"
            >
              더보기
              <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
            {eventsQuery.data!.events.slice(0, 3).map((e) => {
              const daysLeft = Math.max(
                0,
                Math.ceil((new Date(e.ends_at).getTime() - Date.now()) / 86400000),
              );
              return (
                <Link
                  key={e.id}
                  to={`/events/${e.id}`}
                  className="block min-w-0 overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)]/40 p-4 hover:border-[var(--color-primary)]/40"
                >
                  <div className="mb-1.5 flex min-w-0 items-center gap-1.5 overflow-hidden">
                    {/* 상시 이벤트면 "상시" 칩, 그 외는 D-N 칩 */}
                    <span className="shrink-0 rounded bg-[var(--color-rise)]/10 px-1.5 py-0.5 text-[11px] font-bold text-[var(--color-rise)]">
                      {e.is_perpetual ? '상시' : `D-${daysLeft}`}
                    </span>
                    {e.reward_points > 0 && (
                      <span className="shrink-0 rounded bg-[var(--color-primary)]/10 px-1.5 py-0.5 text-[11px] font-bold text-[var(--color-primary)]">
                        최대 {e.reward_points.toLocaleString('ko-KR')}P
                      </span>
                    )}
                  </div>
                  <div className="min-w-0 overflow-hidden truncate text-sm font-semibold">{e.title}</div>
                  <div className="mt-1 min-w-0 overflow-hidden truncate text-xs text-[var(--color-text-sub)]">
                    {e.is_perpetual
                      ? `${e.starts_at.slice(0, 10)} · 상시 진행`
                      : `${e.starts_at.slice(0, 10)} ~ ${e.ends_at.slice(0, 10)}`}
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* 시스템 자동 수집 — 코인시황 / 국내주식 / 공시뉴스 (3열) */}
      <div className="mb-6 grid gap-5 md:mb-10 md:grid-cols-3">
        {SYSTEM_BOARDS.map((b, i) => {
          const q = systemBoardQueries[i];
          const posts = q?.data?.posts ?? [];
          return (
            <section
              key={b.slug}
              className="min-w-0 overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4"
            >
              <div className="mb-3 flex items-center justify-between gap-2">
                <h2 className="text-lg font-extrabold md:text-xl">{b.name}</h2>
                <Link
                  to={`/board/${b.slug}`}
                  className="flex shrink-0 items-center gap-0.5 rounded-full bg-[var(--color-bg)] px-3 py-1 text-xs font-semibold text-[var(--color-text-sub)] hover:text-[var(--color-text)]"
                >
                  더보기
                  <ChevronRight className="h-3.5 w-3.5" />
                </Link>
              </div>
              {q?.isLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 4 }).map((_, j) => (
                    <Skeleton key={j} className="h-10 w-full" />
                  ))}
                </div>
              ) : posts.length === 0 ? (
                <div className="py-6 text-center text-xs text-[var(--color-text-sub)]">
                  아직 게시글이 없어요.
                </div>
              ) : (
                <ul className="divide-y divide-[var(--color-border)]">
                  {posts.slice(0, 5).map((p) => (
                    <li key={p.id} className="min-w-0 w-full overflow-hidden">
                      <Link
                        to={p.event_id ? `/events/${p.event_id}` : `/post/${p.id}`}
                        className="-mx-2 flex min-w-0 w-full items-center gap-2 overflow-hidden rounded-lg px-2 py-2 hover:bg-[var(--color-bg)]"
                      >
                        <span className="min-w-0 flex-1 truncate whitespace-nowrap overflow-hidden text-sm">{p.title}</span>
                        {p.comment_count > 0 && (
                          <span className="shrink-0 text-xs font-semibold text-[var(--color-primary)]">
                            [{p.comment_count}]
                          </span>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>

      {/* 하단: 모두의 매매일지 — 전체 너비 1단 (자유게시판은 인기글/최신글 직후로 이동) */}

      {/* 모두의 매매일지 (컴팩트) */}
      <section className="min-w-0 overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-extrabold md:text-xl">모두의 매매일지</h2>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={goMyDiary}
              className="rounded-full bg-[var(--color-primary)]/10 px-3 py-1 text-xs font-semibold text-[var(--color-primary)] hover:bg-[var(--color-primary)]/15"
            >
              내 매매일지
            </button>
            <Link
              to="/board/diary"
              className="flex items-center gap-0.5 rounded-full bg-[var(--color-bg)] px-3 py-1 text-xs font-semibold text-[var(--color-text-sub)] hover:text-[var(--color-text)]"
            >
              전체 보기
              <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
        {diaryFeed.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : (diaryFeed.data?.feed?.length ?? 0) > 0 ? (
          <ul className="divide-y divide-[var(--color-border)]">
            {diaryFeed.data!.feed.map((d) => {
              const isSell = !!d.sell_date && !!d.sell_price;
              const rate = Number(d.profit_rate ?? 0);
              const profit = Number(d.profit ?? 0);
              // 시간 표시는 created_at 기준 (buy_date/sell_date는 DATE 타입이라 TZ 불일치 발생)
              const date = d.created_at;
              return (
                <li key={d.id} className="min-w-0 w-full overflow-hidden">
                  <Link
                    to={`/diary/public/${encodeURIComponent(d.ticker)}`}
                    className="-mx-2 flex min-w-0 w-full items-center gap-2 overflow-hidden rounded-lg px-2 py-1.5 hover:bg-[var(--color-bg)]"
                  >
                    <Avatar src={d.profile_image_url ?? undefined} alt={d.nickname} size="sm" />
                    <div className="min-w-0 w-full flex-1 overflow-hidden">
                      {/* 닉네임 + Lv — Lv 를 닉네임 바로 옆에 붙임 (flex-1 제거) */}
                      <div className="flex min-w-0 w-full items-center gap-1 overflow-hidden">
                        <span className="max-w-[70%] truncate whitespace-nowrap overflow-hidden text-sm font-semibold">
                          {d.nickname}
                        </span>
                        <span className="shrink-0">
                          <LevelBadge level={d.level} size="sm" />
                        </span>
                      </div>
                      <div className="mt-0.5 flex min-w-0 w-full items-center gap-1.5 overflow-hidden text-xs text-[var(--color-text-sub)]">
                        <span className={isSell ? 'badge badge-fall shrink-0' : 'badge badge-rise shrink-0'}>
                          {isSell ? '매도' : '매수'}
                        </span>
                        <span className="min-w-0 flex-1 truncate whitespace-nowrap overflow-hidden font-medium text-[var(--color-text)]">
                          {diaryDisplayName(d)}
                        </span>
                        {/* 날짜 앞 가운데점 제거 */}
                        <span className="shrink-0">{timeAgo(date)}</span>
                      </div>
                    </div>
                    {isSell && (
                      <div className="shrink-0 text-right">
                        <div
                          className={`flex items-center justify-end gap-0.5 text-sm font-bold tabular-nums ${
                            rate > 0
                              ? 'text-[var(--color-rise)]'
                              : rate < 0
                              ? 'text-[var(--color-fall)]'
                              : 'text-[var(--color-text-sub)]'
                          }`}
                        >
                          {rate > 0 ? (
                            <TrendingUp className="h-3.5 w-3.5" />
                          ) : rate < 0 ? (
                            <TrendingDown className="h-3.5 w-3.5" />
                          ) : null}
                          {rate > 0 ? '+' : ''}
                          {rate.toFixed(2)}%
                        </div>
                        <div className="text-xs text-[var(--color-text-sub)] tabular-nums">
                          {profit >= 0 ? '+' : ''}
                          {comma(Math.round(profit))}원
                        </div>
                      </div>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="py-6 text-center text-sm text-[var(--color-text-sub)]">
            공개된 매매일지가 아직 없어요.
          </div>
        )}
      </section>

      {/* 광고: 홈 최하단 디스플레이 직사각형 (#1 display-rect 3110276859).
          이전 multiplex-h 가 모바일에서 세로로 길게 늘어나 정형 디스플레이로 교체. */}
      <div className="ad-slot mt-3 min-h-[80px] overflow-hidden bg-[var(--color-surface)] sm:min-h-[120px] md:mt-10">
        <AdBanner slot="display-rect" />
      </div>
    </div>
  );
}

/**
 * 메인 상단 히어로 스와이퍼 — 3장 슬라이드 (auto-rotate 5s + 터치 스와이프).
 *  1. 매매일지 유도
 *  2. 종목 분석 유도
 *  3. 커뮤니티 소개 (기존 배너에서 '모의투자' 제거)
 * 외부 swiper 라이브러리 미사용 — translateX 기반 자체 구현으로 번들 가벼움 유지.
 */
function HeroSwiper() {
  interface Slide {
    title: string;
    subtitle: string;
    cta?: string;
    to?: string;
    /** Tailwind gradient 클래스 — bg-gradient-to-br 와 함께 사용 */
    gradient: string;
  }
  const slides: Slide[] = [
    {
      title: '주식의 기본, 매매일지 작성',
      subtitle: '매매 기록으로 손실은 줄이고 수익은 늘려보세요',
      cta: '매매일지 작성하기',
      to: '/diary',
      gradient: 'from-[#1e40af] to-[#3b82f6]',
    },
    {
      title: '매수 전에 종목 분석 확인하자',
      subtitle: 'AI 종목 리포트 · 공시 분석 · 증시뉴스 한눈에',
      cta: '종목 분석 보러가기',
      to: '/news',
      gradient: 'from-[#0e7490] to-[#0ea5e9]',
    },
    {
      title: '공시도 분석하자',
      subtitle: '복잡한 공시도 AI를 통해 편하게 확인',
      cta: '공시 분석 보러가기',
      to: '/board/notice',
      gradient: 'from-[#7c3aed] to-[#a855f7]',
    },
    {
      title: '주식·코인·재테크 종합 커뮤니티',
      subtitle: '투자 캘린더 · 매매일지 · 포인트 리워드',
      gradient: 'from-[var(--color-primary)] to-[#2a4f7a]',
    },
  ];

  const [idx, setIdx] = useState(0);
  const total = slides.length;

  // 5초마다 자동 회전 (사용자 인터랙션 시 재시작 가능하지만 단순화 위해 일정 주기 유지)
  useEffect(() => {
    const timer = setInterval(() => setIdx((i) => (i + 1) % total), 5000);
    return () => clearInterval(timer);
  }, [total]);

  // 터치 스와이프 — 좌/우 50px 이상 이동 시 슬라이드 변경
  const touchStartX = useRef<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null) return;
    const diff = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(diff) > 50) {
      if (diff < 0) setIdx((i) => (i + 1) % total);
      else setIdx((i) => (i - 1 + total) % total);
    }
    touchStartX.current = null;
  };

  const goPrev = () => setIdx((i) => (i - 1 + total) % total);
  const goNext = () => setIdx((i) => (i + 1) % total);

  return (
    /* 모바일에서 가로 overflow 차단:
       - 부모 컨테이너에 w-full + max-w-full 명시 → 부모 grid/flex 안에서도 폭 고정
       - 슬라이드 트랙에 w-full → 트랙 자체 너비를 부모(이 컨테이너) 100% 로 고정
         (자식 sum 으로 트랙이 늘어나면 translateX(-100%) 가 너무 멀리 이동해 화면이 깨짐)
       - 각 슬라이드에 min-w-full shrink-0 → 자식이 부모 트랙 너비만큼만 차지하도록 강제 */
    <div
      className="group/swiper relative h-[120px] min-h-[120px] w-full max-w-full overflow-hidden rounded-2xl border border-[var(--color-border)]"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* 슬라이드 트랙 — translateX 로 가로 이동. w-full 로 트랙 너비를 부모에 고정 */}
      <div
        className="flex h-full w-full transition-transform duration-500 ease-out"
        style={{ transform: `translateX(-${idx * 100}%)` }}
      >
        {slides.map((s, i) => {
          // 좌(텍스트) + 우(CTA 칩) 가로 배치 — 세로 높이 증가 방지로 우측 광고(350×120)와 정렬 유지
          const body = (
            <div
              className={`flex h-full w-full items-center gap-4 overflow-hidden bg-gradient-to-br px-5 text-white sm:px-8 md:px-12 ${s.gradient}`}
            >
              <div className="min-w-0 flex-1">
                <h2 className="truncate text-base font-extrabold leading-tight sm:text-lg md:text-xl">
                  {s.title}
                </h2>
                <p className="mt-1 truncate text-[11px] opacity-95 sm:text-xs md:text-sm">
                  {s.subtitle}
                </p>
              </div>
              {s.cta && (
                <span className="hidden shrink-0 items-center gap-1 rounded-full bg-white/20 px-3 py-1.5 text-xs font-semibold backdrop-blur-sm sm:inline-flex">
                  {s.cta}
                  <ChevronRight className="h-3.5 w-3.5" />
                </span>
              )}
            </div>
          );
          if (s.to) {
            return (
              <Link key={i} to={s.to} className="block h-full min-w-full shrink-0">
                {body}
              </Link>
            );
          }
          return (
            <div key={i} className="h-full min-w-full shrink-0">
              {body}
            </div>
          );
        })}
      </div>

      {/* PC 좌/우 네비게이션 버튼 — md+ 노출, 호버 시 가시성 강화 */}
      <button
        type="button"
        aria-label="이전 슬라이드"
        onClick={goPrev}
        className="absolute left-2 top-1/2 z-10 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/25 text-white opacity-70 transition-opacity hover:bg-black/45 hover:opacity-100 md:flex"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        type="button"
        aria-label="다음 슬라이드"
        onClick={goNext}
        className="absolute right-2 top-1/2 z-10 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/25 text-white opacity-70 transition-opacity hover:bg-black/45 hover:opacity-100 md:flex"
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      {/* 인디케이터 — 하단 중앙. 활성 dot 은 가로로 길게 */}
      <div className="pointer-events-none absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1.5">
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`${i + 1}번 슬라이드로 이동`}
            onClick={() => setIdx(i)}
            className={`pointer-events-auto h-1.5 rounded-full transition-all ${
              i === idx ? 'w-6 bg-white' : 'w-1.5 bg-white/50'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * 출석 유도 배너 — 메인 상단.
 * - 비로그인: 로그인 유도
 * - 로그인 + 오늘 미출석: 즉시 출석하기 버튼
 * - 로그인 + 출석 완료: 숨김
 */
function CheckinBanner() {
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const setLoginModalOpen = useUIStore((s) => s.setLoginModalOpen);
  const queryClient = useQueryClient();
  const addToast = usePointStore((s) => s.addToQueue);

  interface CheckinStatus {
    checkedToday: boolean;
    streak: number;
    nextBonus: { days: number; amount: number } | null;
  }

  const { data: status, isLoading: isStatusLoading } = useQuery({
    queryKey: queryKeys.point.checkinStatus,
    queryFn: () => get<CheckinStatus>('/points/checkin/status'),
    staleTime: 60_000,
    enabled: isLoggedIn,
  });

  /**
   * 이번 달 출석 — replaceable_date(대체 가능 날짜) + alternative_count_this_week 만 쓰려고 호출.
   * CheckinCard 와 동일한 queryKey ['attendance', 'me', month] 이라 캐시 공유.
   */
  const monthStr = (() => {
    const kst = new Date(Date.now() + 9 * 60 * 60 * 1000);
    return kst.toISOString().slice(0, 7);
  })();
  interface AttendanceLite {
    alternative_count_this_week: number;
    replaceable_date: string | null;
  }
  const { data: attendanceMeta } = useQuery({
    queryKey: ['attendance', 'me', monthStr],
    queryFn: () => get<AttendanceLite>(`/points/attendance/me?month=${monthStr}`),
    staleTime: 60_000,
    enabled: isLoggedIn,
  });
  const replaceableDate = attendanceMeta?.replaceable_date ?? null;
  const altCount = attendanceMeta?.alternative_count_this_week ?? 0;
  const ALT_WEEKLY_LIMIT = 2;
  const canUseAltCheckin = !!replaceableDate && altCount < ALT_WEEKLY_LIMIT;

  const checkinMutation = useMutation({
    mutationFn: () =>
      post<{
        success: boolean;
        alreadyDone?: boolean;
        balance?: number;
        earned?: number;
        bonusEarned?: number;
        streak?: number;
      }>('/points/checkin'),
    onSuccess: (result) => {
      if (result.alreadyDone) {
        toast('이미 출석 완료');
      } else {
        const earned = result.earned ?? 5;
        addToast(earned, `${result.streak}일 연속 출석`);
        toast.success(`출석체크 완료! +${earned}P 지급됐어요 🎉`);
        if (result.bonusEarned && result.bonusEarned > 0) {
          setTimeout(() => {
            addToast(result.bonusEarned!, `${result.streak}일 연속 달성 보너스`);
            toast.success(
              `${result.streak}일 연속 출석! +${result.bonusEarned}P 보너스 지급됐어요 🎉`,
            );
          }, 800);
        }
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.point.checkinStatus });
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me });
      queryClient.invalidateQueries({ queryKey: queryKeys.point.me });
      queryClient.invalidateQueries({ queryKey: ['point', 'history'] });
      queryClient.invalidateQueries({ queryKey: ['attendance', 'me'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message ?? '출석체크에 실패했어요.');
    },
  });

  /* 로그인 상태에서 출석 정보 로딩 중 — 깜빡임 방지 위해 배너 숨김 */
  if (isLoggedIn && isStatusLoading) return null;
  /* 로그인 + 이미 출석 완료: 배너 숨김 */
  if (isLoggedIn && status?.checkedToday) return null;

  return (
    <section className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-4 sm:px-5">
      <div className="min-w-0 flex-1 basis-full sm:basis-auto">
        {!isLoggedIn ? (
          <>
            <p className="text-sm font-semibold text-[var(--color-text)]">
              매일 출석하고 포인트 받으세요
            </p>
            <p className="mt-0.5 text-xs text-[var(--color-text-sub)]">
              7일 연속 <span className="font-semibold text-[var(--color-text)]">+50P</span> ·
              30일 연속 <span className="font-semibold text-[var(--color-text)]">+300P</span> 보너스
            </p>
          </>
        ) : (
          <>
            <p className="text-sm font-semibold text-[var(--color-text)]">
              오늘 출석체크 안 하셨어요 <span className="num text-[var(--color-primary)]">+10P</span>
            </p>
            {status?.nextBonus && status.streak > 0 && (
              <p className="mt-0.5 text-xs text-[var(--color-text-sub)]">
                <span className="num font-semibold text-[var(--color-text)]">{status.streak}일</span>{' '}
                연속 · {status.nextBonus.days}일까지{' '}
                <span className="num">{status.nextBonus.days - status.streak}일</span> 남음
              </p>
            )}
            {canUseAltCheckin && replaceableDate && (
              <p className="mt-0.5 text-xs font-medium text-[var(--color-text-sub)]">
                오늘 활동하면 {replaceableDate.slice(5, 7)}월{' '}
                {replaceableDate.slice(8, 10)}일 출석이 자동 인정돼요
              </p>
            )}
          </>
        )}
      </div>
      <Button
        size="md"
        onClick={() => {
          if (!isLoggedIn) return setLoginModalOpen(true);
          checkinMutation.mutate();
        }}
        loading={checkinMutation.isPending}
        className="w-full sm:w-auto"
      >
        {isLoggedIn ? '출석하기' : '로그인하고 출석하기'}
      </Button>
    </section>
  );
}
